import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { authAPI, callsAPI, flowsAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { apiError } from '../../lib/host';
import { prettyNumber } from './CallDialog';
import { CallingBanner } from './calls/components/CallBadges';
import { ErrorBanner, PageHeading, RolePill, StatCards, useBasic } from './ui';

const TIMEOUTS = [15, 20, 30, 45, 60];

export default function PhoneNumbersPage() {
  const navigate = useNavigate();
  const { notify } = useBasic();
  const { refresh } = useAuth();
  const { can } = useTenant();
  const canEdit = can('settings.write');
  const [status, setStatus] = useState(null);
  const [routing, setRouting] = useState(null);
  const [myPhone, setMyPhone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [flows, setFlows] = useState([]);

  // Each form only refreshes its own fields, so saving one never discards unsaved edits in the other.
  const load = useCallback((part = 'all') => {
    callsAPI.telephony().then((res) => {
      setStatus(res.data);
      if (part !== 'phone') setRouting(res.data.routing);
      if (part !== 'routing') setMyPhone(res.data.my_phone || '');
    }).catch((err) => setError(apiError(err, 'Could not load phone settings')));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { flowsAPI.list().then((res) => setFlows(res.data)).catch(() => {}); }, []);

  if (!status) return <section className="page-view"><PageHeading title="Phone Numbers" subtitle="Loading…" /><ErrorBanner message={error} /></section>;

  // Same order the call engine uses: a live flow for this number, then a live flow for all numbers.
  const flowFor = (number) => flows.find((f) => f.is_published && f.number === number) || flows.find((f) => f.is_published && !f.number);
  const reachable = status.team.filter((t) => t.phone);
  const ownersAndAdmins = reachable.filter((t) => t.role === 'owner' || t.role === 'admin').map((t) => t.user_id);
  const ringing = routing.ring_user_ids ?? ownersAndAdmins;
  const toggle = (id) => setRouting({ ...routing, ring_user_ids: ringing.includes(id) ? ringing.filter((x) => x !== id) : [...ringing, id] });

  const run = async (fn, ok, part) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      notify(ok);
      load(part);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveRouting = (e) => {
    e.preventDefault();
    run(() => callsAPI.saveRouting({ ...routing, ring_user_ids: ringing }), 'Call routing saved.', 'routing');
  };

  const saveMyPhone = (e) => {
    e.preventDefault();
    run(async () => { await authAPI.updateMe({ phone: myPhone }); await refresh(); }, 'Your calling number is saved.', 'phone');
  };

  return (
    <section className="page-view">
      <PageHeading title="Phone Numbers" subtitle="Your company numbers and who answers incoming calls.">
        <button className="secondary-action" onClick={() => navigate('/calls')}>Call history <ArrowRight size={13} /></button>
      </PageHeading>
      <CallingBanner status={status} />
      <ErrorBanner message={error} />

      <StatCards stats={[
        ['Phone service', status.configured ? 'Connected' : 'Not connected', status.configured ? 'Plivo' : 'Waiting for setup'],
        ['Company numbers', status.numbers.length, status.numbers.length ? 'Assigned to you' : 'Ask support to assign one'],
        ['Ring for incoming calls', ringing.length, `${routing.ring_timeout} seconds before voicemail`],
        ['Your calling number', status.my_phone ? prettyNumber(status.my_phone) : 'Not set', 'We ring this for click-to-call'],
      ]} />

      <section className="panel page-table-panel">
        <div className="panel-heading"><div><h2>Company numbers</h2><small className="table-subtitle">Customers see these numbers when you call them</small></div></div>
        <div className="table-scroll">
          <table className="full-page-table">
            <thead><tr><th>Number</th><th>Label</th><th>Incoming calls</th><th>Status</th></tr></thead>
            <tbody>
              {status.numbers.map((n) => (
                <tr key={n.id}>
                  <td><b className="cell-strong">{prettyNumber(n.number)}</b></td>
                  <td>{n.label || '—'}</td>
                  <td>
                    {flowFor(n.number)
                      ? <>Call flow: <button className="link-button" onClick={() => navigate(`/call-flows/${flowFor(n.number).id}`)}>{flowFor(n.number).name}</button></>
                      : <>Standard routing: ring {ringing.length} team member{ringing.length === 1 ? '' : 's'}{routing.voicemail ? ', then voicemail' : ''}</>}
                  </td>
                  <td><span className={`status ${status.configured ? 'green' : 'pending'}`}>{status.configured ? 'Active' : 'Waiting for connection'}</span></td>
                </tr>
              ))}
              {status.numbers.length === 0 && <tr><td colSpan={4} className="empty-state">No numbers yet. TeleCalX support assigns numbers to your company.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <div className="page-bottom-grid">
        <form className="panel page-info-card" onSubmit={saveRouting}>
          <div className="panel-heading"><h2>Standard routing</h2>{!canEdit && <span className="cell-muted">Only admins can change this</span>}</div>
          <p className="access-copy" style={{ marginTop: 0 }}>Used for numbers without a live call flow. For menus and branches, design a <button type="button" className="link-button" onClick={() => navigate('/call-flows')}>call flow</button>.</p>
          <fieldset className="form-grid" disabled={!canEdit || busy}>
            <div className="field span-2">Ring these team members together
              <div className="ring-list">
                {status.team.map((t) => (
                  <label key={t.user_id} className={`ring-member ${t.phone ? '' : 'disabled'}`}>
                    <input type="checkbox" disabled={!t.phone} checked={ringing.includes(t.user_id)} onChange={() => toggle(t.user_id)} />
                    <span><b>{t.name}</b><small>{t.phone ? prettyNumber(t.phone) : 'No mobile number'}</small></span>
                    <RolePill role={t.role} />
                  </label>
                ))}
              </div>
            </div>
            <label className="field">Ring for
              <select value={routing.ring_timeout} onChange={(e) => setRouting({ ...routing, ring_timeout: Number(e.target.value) })}>
                {TIMEOUTS.map((s) => <option key={s} value={s}>{s} seconds</option>)}
              </select>
            </label>
            <span />
            <label className="field toggle-field span-2">
              <input type="checkbox" checked={routing.voicemail} onChange={(e) => setRouting({ ...routing, voicemail: e.target.checked })} />
              <span><b>Voicemail</b><small>If nobody answers, callers can leave a message</small></span>
            </label>
            <label className="field toggle-field span-2">
              <input type="checkbox" checked={routing.record_calls} onChange={(e) => setRouting({ ...routing, record_calls: e.target.checked })} />
              <span><b>Record calls</b><small>Every call, call flows included. Callers hear "This call may be recorded". The same as Record calls in Settings › Calling.</small></span>
            </label>
          </fieldset>
          {canEdit && <div className="form-actions"><button type="submit" className="primary-action" disabled={busy}>Save routing</button></div>}
        </form>

        <form className="panel page-info-card" onSubmit={saveMyPhone}>
          <div className="panel-heading"><h2>Your calling number</h2></div>
          <p className="access-copy">When you click Call, TeleCalX rings this phone first, then connects you to the customer. Customers see your company number, not this one.</p>
          <div className="form-grid">
            <label className="field span-2">Your mobile number<input type="tel" required value={myPhone} onChange={(e) => setMyPhone(e.target.value)} placeholder="98765 43210" /></label>
          </div>
          <div className="form-actions"><button type="submit" className="primary-action" disabled={busy || myPhone === (status.my_phone || '')}>Save my number</button></div>
        </form>
      </div>
    </section>
  );
}
